import { ref } from 'vue';
import { useGlobalState } from '@/composables/useGlobalState';

export interface ApiKey {
    id: number;
    name: string;
    scopes: string[];
    createdAt: string;
    lastUsedAt: string | null;
}

export function useApiKeys() {
    const { getAuthHeader } = useGlobalState();
    const apiKeys = ref<ApiKey[]>([]);
    const loading = ref(false);

    const loadApiKeys = async () => {
        loading.value = true;
        try {
            const response = await fetch('/api/sport/api-keys', { headers: getAuthHeader() });
            if (!response.ok) throw new Error('Failed to load API keys');
            apiKeys.value = await response.json();
        } catch (e) {
            console.error(e);
        } finally {
            loading.value = false;
        }
    };

    const createApiKey = async (name: string, scopes: string[]) => {
        const response = await fetch('/api/sport/api-keys', {
            method: 'POST',
            headers: {
                ...getAuthHeader(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name, scopes }),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Erreur lors de la création de la clé');
        }

        const newKey = await response.json();
        // Add to the top of the list
        apiKeys.value.unshift({
            id: newKey.id,
            name: newKey.name,
            scopes: newKey.scopes,
            createdAt: newKey.createdAt,
            lastUsedAt: null,
        });

        return newKey.rawKey;
    };

    const revokeApiKey = async (id: number) => {
        const response = await fetch(`/api/sport/api-keys/${id}`, {
            method: 'DELETE',
            headers: getAuthHeader(),
        });

        if (!response.ok) throw new Error('Failed to revoke API key');

        apiKeys.value = apiKeys.value.filter(k => k.id !== id);
    };

    return {
        apiKeys,
        loading,
        loadApiKeys,
        createApiKey,
        revokeApiKey,
    };
}
